'use client';

import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

interface TableOfContentsProps {
  headings: { id: string; text: string; level: number }[];
}

export default function TableOfContents({ headings }: TableOfContentsProps) {
  const [activeHeading, setActiveHeading] = useState('');

  useEffect(() => {
    const elements = headings
      .map(({ id }) => document.getElementById(id))
      .filter((element): element is HTMLElement => element !== null);
    if (!elements.length) return;

    let frame = 0;
    const update = () => {
      frame = 0;
      let active = '';
      for (const element of elements) {
        if (element.getBoundingClientRect().top > 125) break;
        active = element.id;
      }
      setActiveHeading(active);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      window.cancelAnimationFrame(frame);
    };
  }, [headings]);

  if (!headings.length) return null;

  return (
    <nav aria-label="Article contents">
      <ul className="space-y-3">
        {headings.map(heading => {
          const isActive = activeHeading === heading.id;
          const levelIndent = heading.level <= 2 ? 0 : 12;
          return (
            <li key={heading.id} style={{ paddingLeft: `${levelIndent}px` }}>
              <a
                href={`#${heading.id}`}
                aria-current={isActive ? 'location' : undefined}
                className={cn(
                  'block py-1.5 transition-colors border-l-2 pl-3 -ml-[2px] text-sm',
                  isActive
                    ? 'border-l-blue-600 font-medium text-blue-700'
                    : 'border-l-slate-200 hover:border-l-slate-400 text-slate-500 hover:text-slate-900'
                )}
                onClick={() => setActiveHeading(heading.id)}>
                {heading.text}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
